import { useEffect, useRef } from "react";
import { Dialog } from "./Dialog";
import { useAnalytics } from "../helpers/hooks/use-analytics";

interface FirstBookingCongratsModalProps {
  open: boolean;
  onClose: () => void;
  /** When the first booking landed (the first-booking email's send time). */
  sentAt: string | null;
  /** Opens the in-app assistant (and dismisses the modal) for follow-up questions. */
  onAskAssistant: () => void;
  /**
   * Opens the Insights panel (MarketingNotificationPanel on its insights
   * tab). Called on plain dismissals — customers often miss the Insights
   * entry point, so closing the congrats is the moment we reveal it.
   */
  onOpenInsights: () => void;
}

/**
 * One-time celebration shown on the user's first visit after the "first
 * booking" email goes out (see useFirstBookingCongrats for the gating
 * rules). Carries the same guest-privacy disclaimer as the email so
 * customers don't go hunting for a specific reservation we can't identify,
 * and hands off to the in-app assistant for follow-up questions.
 */
export function FirstBookingCongratsModal({
  open,
  onClose,
  sentAt,
  onAskAssistant,
  onOpenInsights,
}: FirstBookingCongratsModalProps) {
  const { trackEvent } = useAnalytics();
  const hasTrackedOpen = useRef(false);

  useEffect(() => {
    if (open && !hasTrackedOpen.current) {
      trackEvent("first_booking_congrats_shown");
      hasTrackedOpen.current = true;
    }
  }, [open, trackEvent]);

  if (!open) return null;

  const sentAtMs = sentAt ? Date.parse(sentAt) : NaN;
  const receivedOn = Number.isNaN(sentAtMs)
    ? null
    : new Date(sentAtMs).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
      });

  const handleClose = () => {
    trackEvent("first_booking_congrats_dismissed");
    onClose();
    // Deliberately NOT on the "Ask the assistant" path — the chat panel
    // opens there instead, and the two shouldn't fight for attention.
    onOpenInsights();
  };

  const handleAskAssistant = () => {
    trackEvent("first_booking_congrats_ask_assistant");
    onAskAssistant();
  };

  return (
    <>
      {/* Desktop-only pointer to the assistant launcher (fixed bottom-right,
          `.a-launcher` in dashboard-tokens.css at right:22px bottom:22px).
          z-[71] floats it above the Dialog's z-[70] backdrop;
          pointer-events-none keeps the backdrop clickable underneath.
          Hidden on mobile, where the assistant lives in the header instead. */}
      {/* bottom-14 puts the arrow tip just above the launcher (fixed at
          right:22px bottom:22px, 36px round — see .a-launcher). */}
      <div className="hidden lg:flex fixed right-6 bottom-14 z-[71] flex-col items-end gap-1 pointer-events-none">
        <span className="rounded-lg bg-surface-primary border border-border-primary shadow-lg px-3 py-1.5 text-subhead font-medium text-content-primary">
          Your assistant lives here
        </span>
        {/* Hand-drawn-style annotation arrow curving down onto the launcher */}
        <svg
          width="44"
          height="44"
          viewBox="0 0 44 44"
          fill="none"
          className="mr-2 text-content-primary opacity-80"
          aria-hidden="true"
        >
          <path
            d="M6 6c14 0 26 10 29 28"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M27 31l8 3-2-10"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    <Dialog
      variant="center"
      size="sm"
      title="Congrats on your first booking! 🎉"
      onClose={handleClose}
      primaryAction={{
        label: "Got it",
        onClick: handleClose,
        dataAttr: "first-booking-congrats-got-it",
      }}
      secondaryAction={{
        label: "Ask the assistant",
        onClick: handleAskAssistant,
        dataAttr: "first-booking-congrats-ask-assistant",
      }}
    >
      <div className="space-y-3">
        <p className="text-body leading-relaxed text-content-primary">
          Autumn's Google Ads campaigns delivered your first booking
          {receivedOn ? (
            <>
              {" "}
              on <strong>{receivedOn}</strong>
            </>
          ) : null}
          {" "}— the first of many, and a great sign the system is working
          exactly as designed.
        </p>
        <p className="text-subhead leading-relaxed text-content-secondary">
          <strong>One thing to know:</strong> to protect guest privacy, Google
          reports bookings to us as anonymous, aggregated conversions. We can
          see that a booking happened and its value — but never which guest or
          reservation it was. For any further questions, ask the Autumn
          assistant.
        </p>
      </div>
    </Dialog>
    </>
  );
}
